import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { ErrorFallbackScreen } from 'components/screens';
import { PATHS } from 'config/paths';
import { BottomNavShell } from 'modules/footer';
import { GuestCvErrorBoundary } from 'modules/guest-cv/GuestCvErrorBoundary';
import { GuestFormErrorBoundary } from 'modules/guest-forms/GuestFormErrorBoundary';
import { GuestInterviewErrorBoundary } from 'modules/interviews';
import {
  AccountPage,
  AppLandingPage,
  ApplicationDetailPage,
  ApplicationsPage,
  ApplicationStatusLinkPage,
  AssignmentDetailsPage,
  AssignmentsPage,
  AuthenticationErrorPage,
  AvailabilityPage,
  ContactZenjobPage,
  CVBuilderPage,
  DeleteProfilePage,
  GeolocationPermissionsPage,
  GuestCvBuilderPage,
  GuestFormPage,
  GuestInterviewPage,
  JobListingDetailPage,
  LanguageSettingsPage,
  LoginPage,
  NotificationSettingsPage,
  PrivacySettingsPage,
  ReasonDetailsPage,
  ShiftDetailsPage,
  WeekDetailsPage,
  WeeksPage,
  WorkPage
} from 'modules/routes';
import { AuthGuard } from './utils/auth';
import { Auth0ProviderWithNavigate } from './utils/auth/components/Auth0ProviderWithNavigate';

// eslint-disable-next-line react-refresh/only-export-components
const RedirectToWeeks = () => <Navigate to={PATHS.weeks} replace />;

export const routes: RouteObject[] = [
  {
    children: [
      {
        path: PATHS.app,
        element: <AppLandingPage />
      },
      {
        path: PATHS.error,
        element: <AuthenticationErrorPage />
      },
      {
        // Public magic-link CV builder — intentionally OUTSIDE Auth0ProviderWithNavigate
        // so external applicants are never redirected to login. Wrapped in an error boundary
        // because this route has no react-router errorElement (only the auth subtree does),
        // so an uncaught render error would otherwise white-screen the applicant unreported.
        path: PATHS.guestCv,
        element: (
          <GuestCvErrorBoundary>
            <GuestCvBuilderPage />
          </GuestCvErrorBoundary>
        )
      },
      {
        // Public magic-link form runner — same placement and reasoning as the guest CV route
        // above: outside Auth0 so an invited applicant is never redirected to a login they
        // cannot pass, and inside its own error boundary because this route has no
        // react-router errorElement.
        path: PATHS.guestForm,
        element: (
          <GuestFormErrorBoundary>
            <GuestFormPage />
          </GuestFormErrorBoundary>
        )
      },
      {
        // The interview emails' personal link (/interview?t={token}, pro-core's OutboxInterviewMailer). Same placement
        // and reasoning as the guest form route: outside Auth0, inside its own error boundary.
        path: PATHS.guestInterview,
        element: (
          <GuestInterviewErrorBoundary>
            <GuestInterviewPage />
          </GuestInterviewErrorBoundary>
        )
      },
      {
        element: <Auth0ProviderWithNavigate />,
        errorElement: <ErrorFallbackScreen />,
        children: [
          {
            // The bottom navigation lives on this pathless layout route, so it is rendered once for every signed-in
            // page and decides for itself which routes are full-screen flows without it.
            element: <BottomNavShell />,
            children: [
              {
                path: PATHS.root,
                element: <AuthGuard component={RedirectToWeeks} />
              },
              {
                path: PATHS.login,
                element: <LoginPage />
              },
              {
                path: PATHS.cvBuilder,
                element: <AuthGuard component={CVBuilderPage} />
              },
              {
                // "My applications" (ATS). Inside the Auth0 group because the list is the talent's own; both pages
                // hide behind the PRO_ATS tracker arm, so control talents get the not-found screen.
                path: PATHS.applications,
                children: [
                  {
                    index: true,
                    element: <AuthGuard component={ApplicationsPage} />
                  },
                  {
                    path: PATHS.applicationDetails,
                    element: <AuthGuard component={ApplicationDetailPage} />
                  }
                ]
              },
              {
                // One job from the feed (Phase 2). Behind PRO_JOB_LISTING like the feed itself; the feed renders under
                // the applications block on the Jobs page, so there is no list route here, only the detail.
                path: PATHS.jobs,
                children: [
                  {
                    // The list has no route of its own: it renders under the applications block on the Jobs page.
                    index: true,
                    element: <Navigate to={PATHS.applications} replace />
                  },
                  {
                    path: PATHS.jobDetails,
                    element: <AuthGuard component={JobListingDetailPage} />
                  }
                ]
              },
              {
                // Where a status email from pro-core lands. Deliberately outside PATHS.applications: the link is the
                // same for both arms and a control talent must reach their own screen, not the tracker's not-found.
                path: PATHS.applicationStatusLink,
                element: <AuthGuard component={ApplicationStatusLinkPage} />
              },
              {
                path: PATHS.weeks,
                element: <AuthGuard component={WorkPage} />,
                children: [
                  {
                    index: true,
                    element: <WeeksPage />
                  },
                  {
                    path: PATHS.weekDetails,
                    children: [
                      {
                        index: true,
                        element: <WeekDetailsPage />
                      },
                      {
                        path: PATHS.shiftDetails,
                        children: [
                          {
                            index: true,
                            element: <ShiftDetailsPage />
                          }
                        ]
                      },
                      {
                        path: PATHS.reasonDetails,
                        children: [
                          {
                            index: true,
                            element: <ReasonDetailsPage />
                          }
                        ]
                      }
                    ]
                  }
                ]
              },
              {
                path: PATHS.account,
                children: [
                  {
                    index: true,
                    element: <AuthGuard component={AccountPage} />
                  },
                  {
                    path: PATHS.languageSettings,
                    element: <AuthGuard component={LanguageSettingsPage} />
                  },
                  {
                    path: PATHS.privacySettings,
                    children: [
                      {
                        index: true,
                        element: <AuthGuard component={PrivacySettingsPage} />
                      },
                      {
                        path: PATHS.geolocation,
                        element: (
                          <AuthGuard component={GeolocationPermissionsPage} />
                        )
                      }
                    ]
                  },
                  {
                    path: PATHS.contactZenjob,
                    element: <AuthGuard component={ContactZenjobPage} />
                  },
                  {
                    path: PATHS.deleteProfile,
                    element: <AuthGuard component={DeleteProfilePage} />
                  },
                  {
                    path: PATHS.assignments,
                    children: [
                      {
                        index: true,
                        element: <AuthGuard component={AssignmentsPage} />
                      },
                      {
                        path: PATHS.assignmentDetails,
                        element: <AuthGuard component={AssignmentDetailsPage} />
                      }
                    ]
                  },
                  {
                    path: PATHS.notificationSettings,
                    element: <AuthGuard component={NotificationSettingsPage} />
                  },
                  {
                    path: PATHS.availability,
                    element: <AuthGuard component={AvailabilityPage} />
                  }
                ]
              },
              {
                path: '*',
                element: <AuthGuard component={ErrorFallbackScreen} />
              }
            ]
          }
        ]
      }
    ]
  }
];

export const router = createBrowserRouter(routes);
